import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { demeterCompanyApi, demeterUsersApi } from '../../../../Apis/Apis';
import applicationSettings from '../../../../Core/Settings/ApplicationSettings';
import applicationConstants from '../../../../Core/Utility/ApplicationConstants';
import {
    AddDemeterUserRequest,
    AddDemeterUserRequestMarketExpirations,
    DemeterMarket,
    DemeterUserStatus,
    DemeterUserType,
    ExchangeType,
    MarketPrecision,
    SortDirection,
    UpdateDemeterUserRequest,
} from '../../../../Generated/Raven-Demeter';
import { useApplicationSelector } from '../../../../Redux/ReduxStore';
import { selectUserType } from '../../../../Redux/Slices/UserSlice';
import useApi from '../../../Apis/Hooks/useApiHook';
import useApiWithoutAutoExecute from '../../../Apis/Hooks/useApiWithoutAutoExecute';
import LinkButton, { LinkButtonType } from '../../../Components/Form/Buttons/LinkButton';
import Switch from '../../../Components/Form/Buttons/Switch';
import AutoCompleteInput, { AutoCompleteOption } from '../../../Components/Form/Inputs/AutoCompleteInput';
import CheckboxDropdownInput from '../../../Components/Form/Inputs/CheckboxDropdownInput';
import DatePickerInput from '../../../Components/Form/Inputs/DatePickerInput';
import InputContainer from '../../../Components/Form/Inputs/InputContainer';
import SelectInput, { SelectInputOption } from '../../../Components/Form/Inputs/SelectInput';
import TextInput from '../../../Components/Form/Inputs/TextInput';
import PageHeaderWithBackNavigation from '../../../Components/Headers/PageHeaderWithBackNavigation';
import ConfirmModal from '../../../Components/Modals/ConfirmModal/ConfirmModal';
import useNotificationHook from '../../../Components/Notifications/useNotificationHook';
import RegexValidators from '../../../Core/Validation/RegexValidators';
import formattingService from '../../../Services/Formatting/FormattingService';
import useLanguage from '../../../Services/Language/useLanguageHook';
import Permissions from '../Permissions/Permissions';
import {
    areMarketAccessExpirationsEqual,
    buildMarketExpirationsRequest,
    collectMarketsGrantedWithoutEndDate,
    collectPermanentMarkets,
    createMarketAccessExpirationsFromMarketAccesses,
    createMarketAccessPastGrants,
    hasIncompleteMarketAccessExpiration,
    MarketAccessExpiration,
    synchronizeMarketAccessExpirations,
} from './MarketAccess/MarketAccessDefinitions';
import MarketAccessExpirationList from './MarketAccess/MarketAccessExpirationList';
import styles from './UsersPage.module.scss';
import useUserTypeOptions from './useUserTypeOptionsHook';

type ExpirationDateOption = '14' | '30' | '60' | 'custom';

type UpsertDemeterUserRequest = {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    phone?: string | null;
    demeterCompanyGuid?: string | null;
    userType?: DemeterUserType;
    expirationDateOption?: ExpirationDateOption | null;
    trialExpirationDate?: string | Date | null;
    subscribedDate?: string | Date | null;
    marketPrecision?: MarketPrecision;
    realtimeExchanges?: ExchangeType[];
    markets?: DemeterMarket[];
    marketExpirations?: AddDemeterUserRequestMarketExpirations | null;
    isCompanyAdministrator?: boolean;
    notes?: string | null;
};

const realtimeExchanges = [ExchangeType.Cme, ExchangeType.Euronext, ExchangeType.Ice, ExchangeType.Sgx];

// Trial (Regular) users are not paid seats, and Administrators and Business Owners are internal StoneX staff,
// so none of them take a market license away from the company they belong to.
const licenseExemptUserTypes: DemeterUserType[] = [DemeterUserType.Regular, DemeterUserType.Administrator, DemeterUserType.BusinessOwner];

const isLicenseExemptUserType = (userType?: DemeterUserType): boolean => !!userType && licenseExemptUserTypes.includes(userType);

const UserPage: React.FC = () => {
    const { demeterTrialUserGuid } = useParams();
    const navigate = useNavigate();
    const [translations] = useLanguage();
    const [displayError, displaySuccess] = useNotificationHook();
    const currentUserType = useApplicationSelector(selectUserType);
    const isAdministratorOrBusinessOwner = currentUserType === DemeterUserType.Administrator || currentUserType === DemeterUserType.BusinessOwner;

    // User hooks.
    const [user, setUser] = useState<UpsertDemeterUserRequest>();
    const [, , getUserResponse] = useApi(() => {
        if (demeterTrialUserGuid) {
            return demeterUsersApi.getDemeterUser(demeterTrialUserGuid);
        }

        return null;
    });

    const [marketAccessExpirations, setMarketAccessExpirations] = useState<MarketAccessExpiration[]>([]);

    // Company hooks.
    const [companyName, setCompanyName] = useState<string>();
    const [, , listCompaniesResponse] = useApi(() => demeterCompanyApi.listDemeterCompanies(undefined, undefined, SortDirection.Asc, -1, 0));
    const company = useMemo(() => {
        if (!listCompaniesResponse?.rows) {
            return undefined;
        }

        return listCompaniesResponse?.rows?.find((x) => x.name === companyName);
    }, [companyName, listCompaniesResponse]);

    const companyOptions = useMemo((): AutoCompleteOption[] => {
        if (!listCompaniesResponse?.rows) {
            return [];
        }

        return listCompaniesResponse.rows.map((x) => ({
            value: x.demeterCompanyGuid!,
            label: x.name,
            company: x,
        }));
    }, [listCompaniesResponse]);

    // Option hooks.
    const userTypeOptions = useUserTypeOptions(
        false,
        demeterTrialUserGuid ? getUserResponse?.demeterUser?.userType : undefined,
    ) as SelectInputOption<DemeterUserType>[];

    const marketPrecisionOptions = useMemo(
        () => [
            {
                value: null,
                label: '',
            },
            {
                value: MarketPrecision.Realtime,
                label: translations.users.marketPrecision.Realtime,
            },
            {
                value: MarketPrecision.Delayed,
                label: translations.users.marketPrecision.Delayed,
            },
            {
                value: MarketPrecision.EndOfDay,
                label: translations.users.marketPrecision.EndOfDay,
            },
        ],
        [translations],
    ) as { value: MarketPrecision; label: string }[];

    const realtimeExchangeOptions = useMemo(() => realtimeExchanges.map((x) => ({ value: x, label: translations.exchange[x] })), [translations]);
    const marketOptions = useMemo(() => {
        // A company that does not exist yet is created together with this user, so it is not licensed for anything yet.
        const isNewCompany = !!listCompaniesResponse?.rows && !!companyName && !company;

        // License exempt users may take any market regardless of what the company is licensed for,
        // or whether the company exists yet.
        if (isLicenseExemptUserType(user?.userType) || isNewCompany) {
            return [...applicationSettings.markets].sort().map((x) => ({ value: x, label: translations.market[x] }));
        }

        // No company selected means no markets are available to assign.
        if (!company) {
            return [];
        }

        const availableMarkets = company.markets?.sort() ?? [];
        const licensedMarkets = company.marketLicenses;

        if (!licensedMarkets) {
            return availableMarkets.map((x) => ({ value: x, label: translations.market[x] }));
        }

        // Markets already assigned to the user being edited hold a license used by that very user, so they stay available.
        const assignedMarkets = getUserResponse?.demeterUser?.markets ?? [];

        return availableMarkets
            .filter((market) => {
                if (assignedMarkets.includes(market)) {
                    return true;
                }

                const license = licensedMarkets.find((l) => l.market === market);

                return !!license && license.numberOfLicensesUsed < license.numberOfLicenses;
            })
            .map((x) => ({ value: x, label: translations.market[x] }));
    }, [company, companyName, getUserResponse, listCompaniesResponse, user?.userType]);

    const [showResendConfirmModal, setShowResendConfirmModal] = useState(false);
    const [showSectorsWithoutEndDateConfirmModal, setShowSectorsWithoutEndDateConfirmModal] = useState(false);
    const [, resendInvitation] = useApiWithoutAutoExecute(
        () => {
            if (demeterTrialUserGuid) {
                return demeterUsersApi.resendDemeterUserInvitation(demeterTrialUserGuid);
            }

            return null;
        },
        {
            successMessage: translations.users.text.resendTrialInvitationSuccess,
            errorMessage: translations.users.errors.resendTrialInvitationError,
        },
    );

    const showResendTrialInvitation = useMemo(() => {
        if (!demeterTrialUserGuid || !getUserResponse?.demeterUser) {
            return false;
        }

        const { userStatus } = getUserResponse.demeterUser;

        return userStatus === DemeterUserStatus.Pending || userStatus === DemeterUserStatus.PendingApproval;
    }, [demeterTrialUserGuid, getUserResponse]);

    // Actions hooks.
    const [isSaving, setIsSaving] = useState(false);
    const canEditMarketAccessExpirations = isAdministratorOrBusinessOwner && user?.userType === DemeterUserType.Premium;
    // TODO: Perform stronger validation or get the validation from each input.

    const isValid = useMemo(() => {
        if (!user) {
            return false;
        }

        const valid = !!user.email && !!user.userType && !!user.firstName && !!user.lastName && !!companyName;
        if (
            user.userType === DemeterUserType.Regular &&
            (!user.expirationDateOption || (user.expirationDateOption === 'custom' && !user.trialExpirationDate))
        ) {
            return false;
        }

        if (currentUserType === DemeterUserType.Premium) {
            return !!valid;
        }

        const hasIncompleteExpiration = canEditMarketAccessExpirations && hasIncompleteMarketAccessExpiration(marketAccessExpirations);

        return !!(valid && !!user.markets && user.markets.length > 0 && !hasIncompleteExpiration);
    }, [user, companyName, marketAccessExpirations, canEditMarketAccessExpirations]);

    // A sector grant may not outlive the account itself, so the trial expiration caps every period.
    const accountExpirationDate = useMemo(() => {
        if (user?.userType !== DemeterUserType.Regular || !user?.trialExpirationDate) {
            return undefined;
        }

        const parsedDate = new Date(user.trialExpirationDate);

        return Number.isNaN(parsedDate.getTime()) ? undefined : parsedDate;
    }, [user?.userType, user?.trialExpirationDate]);

    // An expired sector is not in the selection but its grant is still on record, so expired grants are read straight from
    // the response rather than from the selection, or they would vanish from the page. They stay read only: putting one back
    // into the selection is a deliberate re-grant and has to go through the market dropdown.
    const marketAccessPastGrants = useMemo(
        () => createMarketAccessPastGrants(user?.markets ?? [], getUserResponse?.demeterUser?.marketAccesses ?? []),
        [user?.markets, getUserResponse],
    );

    const marketsAlreadyGrantedWithoutEndDate = useMemo(
        () =>
            collectPermanentMarkets(
                createMarketAccessExpirationsFromMarketAccesses(
                    getUserResponse?.demeterUser?.markets ?? [],
                    getUserResponse?.demeterUser?.marketAccesses ?? [],
                ),
            ),
        [getUserResponse],
    );

    const marketsGrantedWithoutEndDate = useMemo(
        () => (canEditMarketAccessExpirations ? collectMarketsGrantedWithoutEndDate(marketAccessExpirations, marketsAlreadyGrantedWithoutEndDate) : []),
        [canEditMarketAccessExpirations, marketAccessExpirations, marketsAlreadyGrantedWithoutEndDate],
    );

    // Load the user if there is one.
    useEffect(() => {
        if (!demeterTrialUserGuid && isAdministratorOrBusinessOwner) {
            setUser({ userType: DemeterUserType.Regular, expirationDateOption: '30' });
            return;
        }
        if (!demeterTrialUserGuid && currentUserType === DemeterUserType.Premium) {
            setUser({ userType: DemeterUserType.Premium, subscribedDate: formattingService.toShortDayMonthYear(new Date()) });
            return;
        }

        if (getUserResponse) {
            // The grants are held separately from the editable user fields so they are not echoed back on save.
            const { marketAccesses, ...responseUser } = getUserResponse.demeterUser!;

            setUser({
                ...responseUser,
                expirationDateOption: responseUser.userType === DemeterUserType.Regular ? 'custom' : null,
                trialExpirationDate: responseUser.trialExpirationDate,
                subscribedDate: responseUser.subscribedDate,
                isCompanyAdministrator: responseUser.isCompanyAdministrator,
            });

            setMarketAccessExpirations(createMarketAccessExpirationsFromMarketAccesses(responseUser.markets ?? [], marketAccesses ?? []));
            setCompanyName(responseUser.companyName ?? undefined);
        }
    }, [demeterTrialUserGuid, getUserResponse]);

    useEffect(() => {
        if (!listCompaniesResponse?.rows || !listCompaniesResponse.rows[0]) {
            return;
        }

        if (currentUserType === DemeterUserType.Premium) {
            setCompanyName(listCompaniesResponse.rows[0].name);
        }
    }, [listCompaniesResponse]);

    // A newly selected market starts as permanent, and deselecting a market discards the period that was set for it.
    // Nothing is reconciled until the user exists, otherwise this runs in the same commit that loads them and wipes
    // the periods that were just seeded from the api.
    useEffect(() => {
        if (!user) {
            return;
        }

        setMarketAccessExpirations((currentExpirations) => {
            const synchronizedExpirations = synchronizeMarketAccessExpirations(user?.markets ?? [], currentExpirations);

            return areMarketAccessExpirationsEqual(currentExpirations, synchronizedExpirations) ? currentExpirations : synchronizedExpirations;
        });
    }, [user?.markets]);

    const navigateBackToUsers = () => navigate(-1);

    const upsertUser = async (): Promise<void> => {
        const upsertUserRequest = { ...user, userStatus: undefined };

        upsertUserRequest.marketExpirations = canEditMarketAccessExpirations ? buildMarketExpirationsRequest(marketAccessExpirations) : null;

        if (isValid) {
            try {
                if (companyName) {
                    let demeterCompanyGuid = listCompaniesResponse?.rows?.find((x) => x.name === companyName)?.demeterCompanyGuid;

                    if (!demeterCompanyGuid) {
                        // A new company is set up with the markets its first user is permissioned for. License exempt
                        // users do not consume licenses, every other user type takes one license per market.
                        const markets = upsertUserRequest.markets ?? [];
                        const marketLicenses = isLicenseExemptUserType(upsertUserRequest.userType)
                            ? undefined
                            : markets.map((market) => ({ market, numberOfLicenses: 1, numberOfLicensesUsed: 0 }));

                        const addCompanyResponse = await demeterCompanyApi.addDemeterCompany({
                            name: companyName,
                            markets,
                            marketLicenses,
                            numberOfLicenses: marketLicenses?.length,
                        });
                        demeterCompanyGuid = addCompanyResponse.data.demeterCompanyGuid;
                    }

                    upsertUserRequest.demeterCompanyGuid = demeterCompanyGuid;
                } else {
                    upsertUserRequest.demeterCompanyGuid = null;
                }

                if (upsertUserRequest.userType === DemeterUserType.Regular) {
                    const now = new Date();
                    if (upsertUserRequest.expirationDateOption === '14') {
                        upsertUserRequest.trialExpirationDate = new Date(now.setDate(now.getDate() + 14)).toUTCString();
                    } else if (upsertUserRequest.expirationDateOption === '30') {
                        upsertUserRequest.trialExpirationDate = new Date(now.setDate(now.getDate() + 30)).toUTCString();
                    } else if (upsertUserRequest.expirationDateOption === '60') {
                        upsertUserRequest.trialExpirationDate = new Date(now.setDate(now.getDate() + 60)).toUTCString();
                    } else if (upsertUserRequest.expirationDateOption === 'custom' && upsertUserRequest.trialExpirationDate) {
                        upsertUserRequest.trialExpirationDate = formattingService.toApiDate(upsertUserRequest.trialExpirationDate);
                    }
                } else {
                    upsertUserRequest.trialExpirationDate = null;
                }

                if (upsertUserRequest.subscribedDate) {
                    upsertUserRequest.subscribedDate = formattingService.toApiDate(upsertUserRequest.subscribedDate);
                } else {
                    upsertUserRequest.subscribedDate = null;
                }

                if (!upsertUserRequest.phone || (upsertUserRequest.phone && upsertUserRequest.phone.length === 0)) {
                    upsertUserRequest.phone = null;
                }

                if (demeterTrialUserGuid) {
                    await demeterUsersApi.updateDemeterUser(demeterTrialUserGuid, upsertUserRequest as UpdateDemeterUserRequest);
                    displaySuccess(translations.users.text.userSuccessfullyUpdated);
                } else {
                    await demeterUsersApi.addDemeterUser(upsertUserRequest as AddDemeterUserRequest);
                    displaySuccess(translations.users.text.userSuccessfullyCreated);
                }

                navigateBackToUsers();
            } catch (error) {
                if (demeterTrialUserGuid) {
                    displayError(translations.users.errors.updateUserError);
                } else {
                    const { response } = error as { response?: { status?: number } };
                    const errorMessage =
                        response?.status === applicationConstants.HttpStatus.Conflict
                            ? translations.users.errors.emailAlreadyExistsError
                            : translations.users.errors.addUserError;
                    displayError(errorMessage);
                }
                setIsSaving(false);
            }
        } else {
            setIsSaving(false);
        }
    };

    const startSaving = () => {
        setShowSectorsWithoutEndDateConfirmModal(false);
        // Set this here and let the async upsertUser do its job.
        setIsSaving(true);
        upsertUser();
    };

    // A sector left on permanent saves without an end date, so the administrator confirms which ones before the change is committed.
    const handleSaveClicked = () => {
        if (marketsGrantedWithoutEndDate.length > 0) {
            setShowSectorsWithoutEndDateConfirmModal(true);
            return;
        }

        startSaving();
    };

    return (
        <div className={styles.add_edit_user_page_container}>
            <div className={styles.add_edit_user_header_container}>
                <PageHeaderWithBackNavigation
                    handleBackNavigation={navigateBackToUsers}
                    title={demeterTrialUserGuid ? `${user?.firstName ?? ''} ${user?.lastName ?? ''}` : translations.users.title.add}
                    track={{ eventCategory: 'User', eventAction: 'BackClicked' }}
                />
            </div>
            <div className={styles.add_edit_user_input}>
                <TextInput
                    title={translations.users.fields.firstName}
                    placeholder={translations.users.fields.firstName}
                    required
                    value={user?.firstName}
                    handleTextChange={(value) => setUser({ ...user, firstName: value })}
                    validation={RegexValidators.AlphaNumericMinimumLength2}
                    errorMessage={translations.users.errors.firstNameRequired}
                    track={{
                        eventCategory: 'User',
                        eventAction: 'FirstNameChanged',
                        eventValue: user?.firstName ?? '',
                    }}
                />
            </div>
            <div className={styles.add_edit_user_input}>
                <TextInput
                    title={translations.users.fields.lastName}
                    placeholder={translations.users.fields.lastName}
                    required
                    value={user?.lastName}
                    handleTextChange={(value) => setUser({ ...user, lastName: value })}
                    validation={RegexValidators.AlphaNumericMinimumLength2}
                    errorMessage={translations.users.errors.lastNameRequired}
                    track={{
                        eventCategory: 'User',
                        eventAction: 'LastNameChanged',
                        eventValue: user?.lastName ?? '',
                    }}
                />
            </div>
            <div className={styles.add_edit_user_input}>
                <TextInput
                    disabled={!!demeterTrialUserGuid}
                    title={translations.users.fields.email}
                    placeholder={translations.users.fields.email}
                    required
                    value={user?.email}
                    handleTextChange={(value) => setUser({ ...user, email: value })}
                    validation={RegexValidators.Email}
                    errorMessage={translations.users.errors.emailInvalid}
                    track={{
                        eventCategory: 'User',
                        eventAction: 'EmailChanged',
                        eventValue: user?.email ?? '',
                    }}
                />
            </div>
            <div className={styles.add_edit_user_input}>
                <TextInput
                    title={translations.users.fields.phone}
                    placeholder={translations.users.fields.phone}
                    value={user?.phone}
                    handleTextChange={(value) => setUser({ ...user, phone: value })}
                    validation={RegexValidators.Phone}
                    errorMessage={translations.users.errors.phoneInvalid}
                    track={{
                        eventCategory: 'User',
                        eventAction: 'PhoneChanged',
                        eventValue: user?.phone ?? '',
                    }}
                />
            </div>
            {isAdministratorOrBusinessOwner && (
                <div className={styles.add_edit_user_input}>
                    <AutoCompleteInput
                        title={translations.users.fields.company}
                        labelValue={companyName}
                        options={companyOptions}
                        handleTextChange={(labelValue: string) => setCompanyName(labelValue)}
                        required
                        errorMessage={translations.users.errors.companyNameRequired}
                        track={{ eventCategory: 'User', eventAction: 'CompanyChanged', eventValue: companyName ?? '' }}
                    />
                </div>
            )}
            <div className={styles.add_edit_user_input}>
                <p>
                    <Switch
                        checked={user?.isCompanyAdministrator ?? false}
                        handleChange={(value) => setUser({ ...user, isCompanyAdministrator: value })}
                        track={{
                            eventCategory: 'User',
                            eventAction: 'CompanyAdministratorChanged',
                        }}
                    />
                    {translations.users.fields.companyAdministrator}
                </p>
            </div>
            <div className={styles.add_edit_user_input}>
                {isAdministratorOrBusinessOwner ? (
                    <SelectInput
                        title={translations.users.fields.role}
                        value={user?.userType ?? DemeterUserType.Regular}
                        options={userTypeOptions}
                        handleOptionChange={(value: DemeterUserType) => {
                            // License exempt users may hold markets the company is not licensed for, so drop them
                            // when the role changes to one that has to take a license.
                            const isLosingLicenseExemption = isLicenseExemptUserType(user?.userType) && !isLicenseExemptUserType(value);
                            const isPremiumBecomingTrial = user?.userType === DemeterUserType.Premium && value === DemeterUserType.Regular;
                            setUser({
                                ...user,
                                userType: value,
                                markets: isLosingLicenseExemption ? [] : user?.markets,
                                expirationDateOption: isPremiumBecomingTrial ? '30' : user?.expirationDateOption,
                            });
                        }}
                        track={{ eventCategory: 'User', eventAction: 'UserTypeChanged', eventValue: user?.userType ?? DemeterUserType.Regular }}
                    />
                ) : (
                    <TextInput
                        title={translations.users.fields.role}
                        value={user?.userType ?? DemeterUserType.Premium}
                        disabled
                        track={{
                            eventCategory: 'User',
                            eventAction: 'RoleChanged',
                            eventValue: user?.userType ?? DemeterUserType.Premium,
                        }}
                    />
                )}
            </div>
            {user?.userType === DemeterUserType.Regular && isAdministratorOrBusinessOwner && (
                <>
                    <div>
                        <input
                            id="trialEndDate14"
                            type="radio"
                            onChange={() => setUser({ ...user, expirationDateOption: '14' })}
                            value="14"
                            checked={user.expirationDateOption === '14'}
                        />
                        <label className={styles.add_edit_user_radio_label} htmlFor="trialEndDate14">
                            {translations.users.trialLength.fourteenDays}
                        </label>
                    </div>
                    <div>
                        <input
                            id="trialEndDate30"
                            type="radio"
                            onChange={() => setUser({ ...user, expirationDateOption: '30' })}
                            value="30"
                            checked={user.expirationDateOption === '30'}
                        />
                        <label className={styles.add_edit_user_radio_label} htmlFor="trialEndDate30">
                            {translations.users.trialLength.thirtyDays}
                        </label>
                    </div>
                    <div>
                        <input
                            id="trialEndDateCustom"
                            type="radio"
                            onChange={() => setUser({ ...user, expirationDateOption: 'custom' })}
                            value="custom"
                            checked={user.expirationDateOption === 'custom'}
                        />
                        <label className={styles.add_edit_user_radio_label} htmlFor="trialEndDateCustom">
                            {translations.users.trialLength.custom}
                        </label>
                    </div>
                    {user?.expirationDateOption === 'custom' && (
                        <div className={styles.add_edit_user_input}>
                            <DatePickerInput
                                name="UserExpirationDate"
                                required
                                value={user?.trialExpirationDate as Date}
                                title={translations.users.fields.expiration}
                                handleDateChange={(value) => setUser({ ...user, trialExpirationDate: value })}
                                track={{ eventCategory: 'User', eventAction: 'ExpirationDateChanged', eventValue: user?.trialExpirationDate }}
                            />
                        </div>
                    )}
                </>
            )}
            <div className={styles.add_edit_user_input}>
                <DatePickerInput
                    name="UserSubscibeDate"
                    value={user?.subscribedDate as Date}
                    title={translations.users.fields.subscribedDate}
                    handleDateChange={(value) => setUser({ ...user, subscribedDate: value })}
                    track={{ eventCategory: 'User', eventAction: 'SubscribedDateChanged', eventValue: user?.subscribedDate }}
                />
            </div>
            {isAdministratorOrBusinessOwner && (
                <div className={styles.add_edit_user_input}>
                    <CheckboxDropdownInput
                        title={translations.users.fields.markets}
                        values={user?.markets ?? []}
                        options={marketOptions}
                        handleOptionSelect={(value: DemeterMarket[]) => setUser({ ...user, markets: [...value] })}
                        minimumSelectionLimit={1}
                        track={{ eventCategory: 'User', eventAction: 'MarketChanged', eventValue: user?.markets ?? [] }}
                    />
                </div>
            )}
            {canEditMarketAccessExpirations && (
                <MarketAccessExpirationList
                    expirations={marketAccessExpirations}
                    pastGrants={marketAccessPastGrants}
                    accountExpirationDate={accountExpirationDate}
                    handleExpirationsChange={setMarketAccessExpirations}
                />
            )}
            {isAdministratorOrBusinessOwner && (
                <div className={styles.add_edit_user_input}>
                    <CheckboxDropdownInput
                        title={translations.users.fields.realtimeExchanges}
                        values={user?.realtimeExchanges ?? []}
                        options={realtimeExchangeOptions}
                        handleOptionSelect={(value: ExchangeType[]) => setUser({ ...user, realtimeExchanges: [...value] })}
                        minimumSelectionLimit={1}
                        track={{ eventCategory: 'User', eventAction: 'RealtimeExchangeChanged', eventValue: user?.realtimeExchanges ?? [] }}
                    />
                </div>
            )}
            <div className={styles.add_edit_user_input}>
                <TextInput
                    title={translations.users.fields.notes}
                    placeholder={translations.users.fields.notes}
                    value={user?.notes}
                    handleTextChange={(value) => setUser({ ...user, notes: value })}
                    track={{
                        eventCategory: 'User',
                        eventAction: 'NotesChanged',
                        eventValue: user?.notes,
                    }}
                />
            </div>
            {demeterTrialUserGuid && user && (
                <Permissions demeterUserTrialGuid={demeterTrialUserGuid} userType={user?.userType} demeterCompanyGuid={user?.demeterCompanyGuid ?? undefined} />
            )}
            {getUserResponse?.demeterUser?.termsAndConditionsAcceptedAt && (
                <div className={styles.add_edit_user_input}>
                    <InputContainer title={translations.users.fields.termsAndConditionsAcceptedAt}>
                        {formattingService.toLongDateAndTimeFormat(new Date(getUserResponse?.demeterUser?.termsAndConditionsAcceptedAt))}
                    </InputContainer>
                </div>
            )}
            <div className={styles.add_edit_user_submit_action_row}>
                {showResendTrialInvitation && (
                    <LinkButton
                        title={translations.users.actions.resendTrialInvitation}
                        onClick={() => setShowResendConfirmModal(true)}
                        type={LinkButtonType.White}
                        track={{ eventCategory: 'User', eventAction: 'ResendTrialInvitationClicked' }}
                    />
                )}
                <LinkButton
                    title={translations.actions.cancel}
                    onClick={navigateBackToUsers}
                    type={LinkButtonType.White}
                    track={{ eventCategory: 'User', eventAction: 'CancelClicked' }}
                />
                <LinkButton
                    title={demeterTrialUserGuid ? translations.actions.save : translations.actions.add}
                    type={LinkButtonType.Blue}
                    onClick={handleSaveClicked}
                    disabled={!isValid || isSaving}
                    track={{ eventCategory: 'User', eventAction: 'AddOrSaveClicked' }}
                />
            </div>
            <ConfirmModal
                header={translations.users.text.confirmResendTrialInvitationHeader}
                message={translations.users.text.confirmResendTrialInvitationMessage}
                showModal={showResendConfirmModal}
                handleConfirm={() => {
                    resendInvitation();
                    setShowResendConfirmModal(false);
                }}
                handleCancel={() => setShowResendConfirmModal(false)}
                track={{ eventCategory: 'User', eventAction: 'ModalResendTrialInvitation' }}
            />
            <ConfirmModal
                header={translations.users.text.confirmSectorsWithoutEndDateHeader}
                message={`${translations.users.text.confirmSectorsWithoutEndDateMessage} ${marketsGrantedWithoutEndDate
                    .map((market) => translations.market[market])
                    .join(', ')}`}
                showModal={showSectorsWithoutEndDateConfirmModal}
                handleConfirm={startSaving}
                handleCancel={() => setShowSectorsWithoutEndDateConfirmModal(false)}
                track={{ eventCategory: 'User', eventAction: 'ModalSectorsWithoutEndDate' }}
            />
        </div>
    );
};

export default UserPage;
